/**
 * Player import – shared state (sessionStorage) and data helpers
 * used by PlayerImportModal, PlayerImportMappingView and PlayerImportReviewView.
 */

export type ImportSource = 'sportbm'

export type ImportFieldKey =
  | 'first_name'
  | 'last_name'
  | 'birth_date'
  | 'guardian1_first_name'
  | 'guardian1_last_name'
  | 'email'
  | 'phone'
  | 'license_number'
  | 'medical_exam_date'
  | 'street'
  | 'house_number'
  | 'apartment_number'
  | 'postal_code'
  | 'city'
  | 'school_name'

export interface ImportField {
  key: ImportFieldKey
  // Column header names in the source file used for auto-mapping
  sportbmHeaders: string[]
  required?: boolean
}

export const IMPORT_FIELDS: ImportField[] = [
  { key: 'first_name', sportbmHeaders: ['imię'], required: true },
  { key: 'last_name', sportbmHeaders: ['nazwisko'], required: true },
  { key: 'birth_date', sportbmHeaders: ['data urodzenia'] },
  { key: 'guardian1_first_name', sportbmHeaders: ['imię opiekuna'] },
  { key: 'guardian1_last_name', sportbmHeaders: ['nazwisko opiekuna'] },
  { key: 'email', sportbmHeaders: ['e-mail opiekuna'] },
  { key: 'phone', sportbmHeaders: ['telefon opiekuna'] },
  { key: 'license_number', sportbmHeaders: ['numer licencji'] },
  { key: 'medical_exam_date', sportbmHeaders: ['termin badań'] },
  { key: 'street', sportbmHeaders: ['ulica'] },
  { key: 'house_number', sportbmHeaders: ['numer domu'] },
  { key: 'apartment_number', sportbmHeaders: ['numer mieszkania'] },
  { key: 'postal_code', sportbmHeaders: ['kod pocztowy'] },
  { key: 'city', sportbmHeaders: ['miasto'] },
  { key: 'school_name', sportbmHeaders: ['szkoła'] },
]

export type ColumnMapping = Record<ImportFieldKey, number | null>

export interface ReviewRow {
  id: number
  selected: boolean
  duplicate: boolean
  category_uuid: string
  gender: '' | 'M' | 'W'
  first_name: string
  last_name: string
  birth_date: string
  email: string
  phone: string
  guardian1_first_name: string
  guardian1_last_name: string
  license_number: string
  medical_exam_date: string
  street: string
  building_number: string
  postal_code: string
  city: string
  school_name: string
}

export interface PlayerImportState {
  clubUuid: string
  source: ImportSource
  fileName: string
  rows: string[][]
  startRow: number // 1-based, as displayed to the user
  mapping: ColumnMapping | null
  reviewRows: ReviewRow[] | null
}

const STORAGE_KEY = 'player_import_state'

export const loadImportState = (clubUuid: string): PlayerImportState | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const state = JSON.parse(raw) as PlayerImportState
    return state.clubUuid === clubUuid ? state : null
  } catch {
    return null
  }
}

export const saveImportState = (state: PlayerImportState) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch (err) {
    console.error('Save import state error:', err)
  }
}

export const clearImportState = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}

const normalizeHeader = (value: string) => value.trim().toLowerCase().replace(/\s+/g, ' ')

// Sport BM marks empty values with "-"
export const cleanValue = (value: string | undefined | null): string => {
  const v = (value ?? '').toString().trim()
  return /^[-–—]+$/.test(v) ? '' : v
}

/**
 * Finds the header row (the one containing "imię" and "nazwisko") and returns
 * the first data row (1-based). Falls back to 1.
 */
export const detectStartRow = (rows: string[][]): number => {
  const index = rows.findIndex(row => {
    const headers = row.map(normalizeHeader)
    return headers.includes('imię') && headers.includes('nazwisko')
  })
  return index >= 0 ? index + 2 : 1
}

/**
 * Auto-maps columns by header names. When the same header appears more than once
 * (Sport BM exports "szkoła" and "numer licencji" twice) the column with more values wins.
 */
export const autoMapColumns = (rows: string[][], startRow: number): ColumnMapping => {
  const headerRow = rows[startRow - 2] || []
  const dataRows = rows.slice(startRow - 1)
  const filled = (col: number) => dataRows.filter(r => cleanValue(r[col]) !== '').length

  const mapping = {} as ColumnMapping
  for (const field of IMPORT_FIELDS) {
    const candidates = headerRow
      .map((header, col) => ({ header: normalizeHeader(header || ''), col }))
      .filter(c => field.sportbmHeaders.includes(c.header))
      .map(c => c.col)

    mapping[field.key] = candidates.length
      ? candidates.reduce((best, col) => (filled(col) > filled(best) ? col : best))
      : null
  }
  return mapping
}

/**
 * Converts dates like "07.07.2012", "07/07/2012", "07-07-2012" or "2012-07-07" (optionally with time) to YYYY-MM-DD.
 */
export const normalizeDate = (value: string): string => {
  const v = cleanValue(value)
  if (!v) return ''

  let m = v.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/)
  if (m) return formatDate(Number(m[1]), Number(m[2]), Number(m[3]))

  m = v.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})/)
  if (m) return formatDate(Number(m[3]), Number(m[2]), Number(m[1]))

  return ''
}

const formatDate = (year: number, month: number, day: number): string => {
  const date = new Date(Date.UTC(year, month - 1, day))
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return ''
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

// "11/" + "14" => "11/14"
export const buildBuildingNumber = (house: string, apartment: string): string => {
  const h = cleanValue(house).replace(/\/+$/, '').trim()
  const a = cleanValue(apartment).replace(/^\/+/, '').trim()
  if (h && a) return `${h}/${a}`
  return h || a
}

export const buildReviewRows = (rows: string[][], startRow: number, mapping: ColumnMapping): ReviewRow[] => {
  const get = (row: string[], key: ImportFieldKey) => {
    const col = mapping[key]
    return col === null || col === undefined ? '' : cleanValue(row[col])
  }

  return rows
    .slice(startRow - 1)
    .filter(row => get(row, 'first_name') || get(row, 'last_name'))
    .map((row, index) => ({
      id: index,
      selected: false,
      duplicate: false,
      category_uuid: '',
      gender: 'M',
      first_name: get(row, 'first_name'),
      last_name: get(row, 'last_name'),
      birth_date: normalizeDate(get(row, 'birth_date')),
      email: get(row, 'email').toLowerCase(),
      phone: get(row, 'phone').replace(/\s+/g, ''),
      guardian1_first_name: get(row, 'guardian1_first_name'),
      guardian1_last_name: get(row, 'guardian1_last_name'),
      license_number: get(row, 'license_number'),
      medical_exam_date: normalizeDate(get(row, 'medical_exam_date')),
      street: get(row, 'street'),
      building_number: buildBuildingNumber(get(row, 'house_number'), get(row, 'apartment_number')),
      postal_code: get(row, 'postal_code').replace(/^(\d{2})(\d{3})$/, '$1-$2'),
      city: get(row, 'city'),
      school_name: get(row, 'school_name'),
    }))
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const POSTAL_CODE_REGEX = /^\d{2}-\d{3}$/

export type RowErrors = Partial<Record<keyof ReviewRow, boolean>>

export const validateRow = (row: ReviewRow): RowErrors => {
  const errors: RowErrors = {}
  if (!row.first_name.trim()) errors.first_name = true
  if (!row.last_name.trim()) errors.last_name = true
  if (!row.gender) errors.gender = true
  if (row.email && !EMAIL_REGEX.test(row.email)) errors.email = true
  if (row.postal_code && !POSTAL_CODE_REGEX.test(row.postal_code)) errors.postal_code = true
  if (row.phone.length > 20) errors.phone = true
  if (row.building_number.length > 20) errors.building_number = true
  if (row.city.length > 100) errors.city = true
  if (row.license_number.length > 50) errors.license_number = true
  return errors
}

// Payload for POST v1/{club}/players/import. Guardian email/phone = player's email/phone.
export const toImportPayload = (row: ReviewRow) => {
  const nullable = (v: string) => (v.trim() === '' ? null : v.trim())
  return {
    first_name: row.first_name.trim(),
    last_name: row.last_name.trim(),
    gender: row.gender,
    birth_date: nullable(row.birth_date),
    email: nullable(row.email),
    phone: nullable(row.phone),
    license_number: nullable(row.license_number),
    medical_exam_date: nullable(row.medical_exam_date),
    street: nullable(row.street),
    building_number: nullable(row.building_number),
    city: nullable(row.city),
    postal_code: nullable(row.postal_code),
    school_name: nullable(row.school_name),
    guardian1_first_name: nullable(row.guardian1_first_name),
    guardian1_last_name: nullable(row.guardian1_last_name),
    guardian1_email: nullable(row.email),
    guardian1_phone: nullable(row.phone),
    category_uuid: row.category_uuid || null,
  }
}
